import { useState, useRef, useEffect } from 'react';

/**
 * AddressAutocomplete component using Google Places Autocomplete
 * Requires Google Maps API key in REACT_APP_GOOGLE_MAPS_API_KEY
 */
export default function AddressAutocomplete({ value, onChange, placeholder, disabled, className }) {
  const [address, setAddress] = useState(value || '');
  const autocompleteRef = useRef(null);
  const inputRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setAddress(value || '');
  }, [value]);

  useEffect(() => {
    // Load Google Maps Places API script
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      console.warn('REACT_APP_GOOGLE_MAPS_API_KEY is not set');
      return;
    }

    // Check if script is already loaded
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsLoaded(true);
      initializeAutocomplete();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`)) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsLoaded(true);
          initializeAutocomplete();
          clearInterval(checkLoaded);
        }
      }, 100);
      return () => clearInterval(checkLoaded);
    }

    // Load the script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      setIsLoaded(true);
      initializeAutocomplete();
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove autocomplete listener
      if (autocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(autocompleteRef.current);
      }
    };
  }, []);

  const initializeAutocomplete = () => {
    if (!inputRef.current || !window.google?.maps?.places) return;

    const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'us' }, // Restrict to US addresses
    });

    autocompleteRef.current = autocomplete;

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (place.formatted_address) {
        setAddress(place.formatted_address);
        onChange?.(place.formatted_address);
      }
    });
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setAddress(newValue);
    onChange?.(newValue);
  };

  return (
    <input
      ref={inputRef}
      type="text"
      value={address}
      onChange={handleInputChange}
      placeholder={placeholder || 'Enter your address'}
      disabled={disabled}
      className={className || 'w-full rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3 text-lg'}
      autoComplete="off"
    />
  );
}
